import React from "react";
import { X } from "lucide-react";

export type EstadoDeIntegracion = "desconectada" | "conectada" | "vencida";

/** Lo que una integración le cuenta al resumen de mobile (`ResumenDeConexiones`). */
export type ConexionRegistrada = {
  /** Fija el orden en el resumen: Tiendanube, Mercado Libre, Instagram, Anuncios de Meta. */
  clave: "tiendanube" | "mercadolibre" | "instagram" | "meta";
  icono: React.ReactNode;
  nombre: string;
  estado: EstadoDeIntegracion;
  detalle?: string | null;
  onConectar: () => void;
  onDesconectar: () => void;
};

/**
 * Registro de las integraciones montadas, para el resumen de mobile —
 * ADR-2026-10-05-mi-marca-conexiones-en-un-renglon. Cada integración (con su lógica y
 * sus modales, que siguen siendo suyos) se anota acá con su estado; el renglón y la
 * hoja de `ResumenDeConexiones` leen la lista. Sin proveedor (cualquier otro lugar
 * que use el chip) no se anota nada.
 */
export const RegistroDeConexiones = React.createContext<
  ((c: ConexionRegistrada) => () => void) | null
>(null);

/**
 * Una integración de la marca en la cabecera de Mi marca —
 * ADR-2026-10-02-mi-marca-cabecera-de-identidad.
 *
 * Es SÓLO el disparador: Tiendanube, Instagram y Anuncios de Meta lo usan con su
 * propia lógica y sus propios modales (`TiendaNubeStatus`, `InstagramStatus`,
 * `MetaAdsStatus`). Vive aparte para que las tres se lean como una misma familia.
 *
 * En escritorio (`lg+`) es un chip:
 *  • desconectada → fondo blanco con borde punteado, y el chip entero es "Conectar".
 *  • conectada    → punto verde + la cuenta, y una × chica para desconectar (que abre
 *                   la confirmación de cada integración, no desconecta directo).
 *  • vencida      → punto ámbar + "Reconectar".
 *
 * Abajo de `lg` no dibuja nada: se anota en `RegistroDeConexiones` y las tres se
 * muestran juntas en un solo renglón que abre una hoja —
 * ADR-2026-10-05-mi-marca-conexiones-en-un-renglon (supersede
 * ADR-2026-10-05-mi-marca-integraciones-en-tarjetas).
 */
export const ChipDeIntegracion = ({
  clave,
  icono,
  nombre,
  estado,
  detalle,
  onConectar,
  onDesconectar,
}: ConexionRegistrada) => {
  const registrar = React.useContext(RegistroDeConexiones);
  // Los handlers llegan nuevos en cada render (flechas inline): se leen por ref para
  // que el registro no se rehaga en cada vuelta.
  const handlers = React.useRef({ onConectar, onDesconectar });
  handlers.current = { onConectar, onDesconectar };
  React.useEffect(() => {
    if (!registrar) return;
    return registrar({
      clave,
      icono,
      nombre,
      estado,
      detalle,
      onConectar: () => handlers.current.onConectar(),
      onDesconectar: () => handlers.current.onDesconectar(),
    });
    // `icono` es un elemento nuevo en cada render y siempre el mismo dibujo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrar, clave, nombre, estado, detalle]);

  return (
    <Chip
      icono={icono}
      nombre={nombre}
      estado={estado}
      detalle={detalle}
      onConectar={onConectar}
      onDesconectar={onDesconectar}
    />
  );
};

type PropsDelDisparador = Omit<ConexionRegistrada, "clave">;

/** El chip de escritorio (`lg+`). */
const Chip = ({
  icono,
  nombre,
  estado,
  detalle,
  onConectar,
  onDesconectar,
}: PropsDelDisparador) => {
  if (estado === "desconectada") {
    return (
      <button
        type="button"
        onClick={onConectar}
        className="group hidden lg:inline-flex h-10 items-center gap-2 rounded-full border border-dashed border-black/[0.16] bg-white pl-3 pr-1.5 text-[13.5px] font-medium text-[#16181D] shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-colors hover:border-black/30"
      >
        {icono}
        {nombre}
        <span className="ml-0.5 inline-flex h-7 items-center rounded-full bg-[#EBEFFE] px-2.5 text-[12.5px] font-semibold text-brand-primary transition-colors group-hover:bg-[#dfe5fd]">
          Conectar
        </span>
      </button>
    );
  }

  const vencida = estado === "vencida";
  return (
    <div className="hidden lg:inline-flex h-10 max-w-[320px] items-center gap-2 rounded-full border border-black/[0.07] bg-white pl-3 pr-1.5 text-[13.5px] shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
      {icono}
      <span className="shrink-0 font-medium text-[#16181D]">{nombre}</span>
      <span
        aria-hidden
        className={`h-1.5 w-1.5 shrink-0 rounded-full ${vencida ? "bg-amber-500" : "bg-emerald-500"}`}
      />
      <span className="min-w-0 truncate text-black/50">
        {vencida ? "Venció" : detalle || "Conectada"}
      </span>
      {vencida ? (
        <button
          type="button"
          onClick={onConectar}
          className="ml-0.5 inline-flex h-7 shrink-0 items-center rounded-full bg-[#EBEFFE] px-2.5 text-[12.5px] font-semibold text-brand-primary transition-colors hover:bg-[#dfe5fd]"
        >
          Reconectar
        </button>
      ) : (
        <button
          type="button"
          onClick={onDesconectar}
          aria-label={`Desconectar ${nombre}`}
          title="Desconectar"
          className="ml-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-black/35 transition-colors hover:bg-black/[0.05] hover:text-black/70"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};
